import { CustomFiltersModalActions } from '@modules/common/components/CustomFilterModalActions';
import { useFetchCustomFilter } from '@modules/queryBuilder/hooks';
import {
  Alert,
  Box,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { WhereClause } from 'v2/core/services/queryBuilder/whereClause';
import { CustomFilterModalEditQuery } from '../CustomFilterModalEditQuery';
import { CustomFilterModalNameInput } from '../CustomFilterModalNameInput';

interface EditCustomFilterModalProps {
  whereClause?: WhereClause;
  onClose: () => void;
  filterName: string;
  filterID?: string;
}

export const EditCustomFilterModal = ({
  whereClause,
  onClose,
  filterName,
  filterID,
}: EditCustomFilterModalProps) => {
  const [updatedFilterName, setUpdatedFilterName] = useState(filterName);
  const [isError, setIsError] = useState(false);

  const onError = useCallback(() => {
    setIsError(true);
  }, []);

  const [fetchAndApplyFilters, { analysis }] = useFetchCustomFilter({
    onError,
  });

  useEffect(() => {
    if (filterID) {
      setIsError(false);
      fetchAndApplyFilters(filterID);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterID]);

  return (
    <DialogContent sx={{ p: 0 }}>
      <DialogTitle>Edit Custom Filter</DialogTitle>
      <DialogContent dividers>
        <CustomFilterModalNameInput
          filterName={updatedFilterName}
          setFilterName={setUpdatedFilterName}
          actionType="EDIT"
          createdBy={analysis?.createdBy}
        />
        {isError ? (
          <Box component={Paper} p={2}>
            <Typography variant="body2" color="error">
              Failed to fetch the Custom Filter. Please try again later.
            </Typography>
          </Box>
        ) : (
          <CustomFilterModalEditQuery whereClause={whereClause} />
        )}
        <Alert
          severity="info"
          variant="outlined"
          sx={{
            mt: 2,
            '.MuiAlert-icon': {
              alignSelf: 'center',
            },
          }}
        >
          Your edits will affect future use of the custom filter. Existing
          Dashboards, Quantify, and Feed where this filter is used will remain
          unchanged.
        </Alert>
      </DialogContent>
      <DialogActions>
        <CustomFiltersModalActions
          onClose={onClose}
          filterName={updatedFilterName}
          showDeleteButton
          primaryAction="EDIT"
          analysisEditToken={analysis?.editToken}
          analysisID={analysis?.id}
          isDisabled={isError}
        />
      </DialogActions>
    </DialogContent>
  );
};
